import { useState, useEffect } from "react";
import { Loader2, CheckCircle2, Clock, XCircle } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useI18n } from "@/i18n/I18nContext";
import { PublicShell, publicButton } from "@/components/control/PublicShell";

const LINK = /^#(bestaetigen|abbrechen)=([A-Za-z0-9_-]{20,})$/;
const when = (iso) => (iso ? new Date(iso).toLocaleString("de-CH", { dateStyle: "short", timeStyle: "short" }) : "–");

function readLink() {
  const match = typeof window !== "undefined" ? window.location.hash.match(LINK) : null;
  return match ? { kind: match[1], token: match[2] } : null;
}

function Done({ link, result, t }) {
  if (link.kind === "abbrechen") {
    return <p role="status" data-testid="mail-link-cancelled" className="flex gap-2 rounded-lg border border-green-200 bg-green-50 p-4 text-sm text-green-800">
      <XCircle className="h-5 w-5 shrink-0" aria-hidden="true" />{t("login.mail_recovery.link_cancelled")}</p>;
  }
  return (
    <div role="status" data-testid="mail-link-confirmed" className="space-y-2 rounded-lg border border-green-200 bg-green-50 p-4 text-sm text-green-900">
      <p className="flex gap-2"><CheckCircle2 className="h-5 w-5 shrink-0" aria-hidden="true" />{t("login.mail_recovery.link_confirmed", { time: when(result.wait_until) })}</p>
      <p>{t(result.approval_by === "betreiber" ? "login.mail_recovery.link_next_operator" : "login.mail_recovery.link_next_super_admin")}</p>
      <p data-testid="mail-link-warning-state">{t(result.warning_mail === "fehlgeschlagen" ? "login.mail_recovery.link_warning_failed" : "login.mail_recovery.link_warning_sent")}</p>
    </div>
  );
}

export default function TwoFaRecoveryLink() {
  const { t } = useI18n();
  const [link] = useState(readLink);
  const [state, setState] = useState(link ? "ready" : "invalid");
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    // Token lives only in the URL fragment (never in a request URL); read once above, then removed from address bar
    // and history, plus no-referrer. index.html loads no external script/tracking loader; only Google Font CSS is external.
    if (window.location.hash) window.history.replaceState(null, "", window.location.pathname);
    const meta = document.createElement("meta");
    meta.name = "referrer"; meta.content = "no-referrer";
    document.head.appendChild(meta);
    return () => { meta.remove(); };
  }, []);

  const submit = async () => {
    setState("busy"); setError("");
    try {
      const { data } = await api.post(`/auth/2fa/email-recovery/${link.kind === "bestaetigen" ? "confirm" : "cancel"}`, { token: link.token });
      setResult(data); setState("done");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      // One-time link: after 404/409/502 the token is used up or the request has ended, so no retry button.
      setState([404, 409, 502].includes(err.response?.status) ? "invalid" : "ready");
    }
  };

  const confirm = link?.kind === "bestaetigen";
  return (
    <PublicShell title={t("login.mail_recovery.link_title")} testId="mail-link-page"
      subtitle={link ? t(confirm ? "login.mail_recovery.link_confirm_intro" : "login.mail_recovery.link_cancel_intro") : ""}>
      {state === "invalid" && (
        <p role="alert" data-testid="mail-link-invalid" className="flex gap-2 rounded-lg border border-orange-200 bg-orange-50 p-4 text-sm text-orange-800">
          <Clock className="h-5 w-5 shrink-0" aria-hidden="true" />{error || t("login.mail_recovery.link_invalid")}
        </p>
      )}
      {state === "done" && <Done link={link} result={result} t={t} />}
      {(state === "ready" || state === "busy") && (
        <div className="space-y-3">
          {error && <p role="alert" data-testid="mail-link-error" className="text-sm text-red-700">{error}</p>}
          <button type="button" onClick={submit} disabled={state === "busy"} data-testid="mail-link-submit"
            className={confirm ? publicButton : publicButton.replace("bg-brand-magenta", "bg-red-600").replace("hover:bg-brand-magenta-dark", "hover:bg-red-700")}>
            {state === "busy" && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
            {t(confirm ? "login.mail_recovery.link_confirm" : "login.mail_recovery.link_cancel")}
          </button>
        </div>
      )}
    </PublicShell>
  );
}
